import { useMemo, useState } from "react";
import { Archive, ChevronDown, ChevronRight, CircleCheck, CircleX, Clock, DraftingCompass, Target, X } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { Pill } from "@/components/FilterPills";
import { SpecTrackerTable, scopeLabel } from "./SpecTrackerTable";

const SEP = "|";
const EMPTY = { scope: "", architect: "", gc: "", stage: "" };
const picks = (v) => (v ? v.split(SEP) : []);
const options = (rows, get) => {
  const counts = new Map();
  rows.forEach((r) => { const v = get(r); if (v) counts.set(v, (counts.get(v) || 0) + 1); });
  return [...counts.keys()].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v, count: counts.get(v) }));
};

/** Basis of Design board — the sortable table (Scope/Architect filter from their headers). Shared by Sales and Budgets. */
const matches = (r, q) => !q || [r.project_name, r.customer, r.gc_name, r.architect, r.scope_code, r.scope_name, r.scope_alias, r.account_manager,
  r.gc_contact_name, r.gc_contact_email, r.architect_contact_name, r.architect_contact_email, (r.manufacturers || []).join(" ")]
  .join(" ").toLowerCase().includes(q);

const isHit = (r) => ["basis_of_design", "approved_equal"].includes(r.spec_status);
const daysTo = (d) => (d ? Math.ceil((new Date(`${d}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000) : null);

/** Stat band — same cards as Budgets › Dashboard. "Due ≤ 14d, no BOD" = open rows bidding within two weeks that still
 *  aren't Basis of Design / Approved Equal: the AM's call list. Hit rate = projects with a BOD/AE row ÷ all projects. */
function BodStats({ rows }) {
  const n = (st) => rows.filter((r) => r.spec_status === st).length;
  const projects = new Set(rows.map((r) => r.budget_id));
  const hits = new Set(rows.filter(isHit).map((r) => r.budget_id));
  const rate = projects.size ? Math.round((hits.size / projects.size) * 100) : 0;
  const dueSoon = rows.filter((r) => !r.is_archived && !isHit(r) && (() => { const d = daysTo(r.bid_due); return d !== null && d >= 0 && d <= 14; })()).length;
  const cards = [
    ["Basis of Design", n("basis_of_design"), DraftingCompass, "", "bod"],
    ["Approved Equal", n("approved_equal"), CircleCheck, "text-teal-600", "ae"],
    ["Not Listed", n("not_listed"), CircleX, "text-red-600", "not-listed"],
    ["Due ≤ 14d, No BOD", dueSoon, Clock, "text-amber-600", "due-soon"],
    ["BOD Hit Rate", `${rate}%`, Target, "", "hit-rate"],
  ];
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="bod-stats">
      {cards.map(([label, value, Icon, tone, key]) => (
        <div key={key} className="rounded-sm border border-border bg-card p-4 text-card-foreground" data-testid={`bod-stat-${key}`}>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-medium ${tone || "text-muted-foreground"}`}>{label}</span>
            <Icon className={`h-4 w-4 ${tone || "text-muted-foreground"}`} />
          </div>
          <div className={`mt-1 text-2xl font-semibold ${tone || "text-foreground"}`} data-testid={`bod-stat-${key}-value`}>{value}</div>
        </div>))}
    </div>
  );
}

export function BodBoard({ bod, onPatch, extraColumns = false, emptyText, toolbar = true, stats = false, openArchive = false }) {
  const [f, setF] = useState(EMPTY);
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [archToggled, setArchToggled] = useState(null);
  const archOpen = archToggled ?? openArchive;
  const setArchOpen = setArchToggled;
  const gcOf = (r) => r.gc_name || r.customer || "";
  const filterOptions = useMemo(() => ({ scope: options(bod.rows, scopeLabel), architect: options(bod.rows, (r) => r.architect), gc: options(bod.rows, gcOf),
    stage: options(bod.rows, (r) => r.type || "B").map((o) => ({ ...o, label: o.value === "E" ? "E — Estimate" : "B — Budget" })) }), [bod.rows]);
  const q = search.trim().toLowerCase();
  const filtered = bod.rows.filter((r) => (!f.stage || f.stage === (r.type || "B")) && (!f.scope || picks(f.scope).includes(scopeLabel(r))) && (!f.architect || picks(f.architect).includes(r.architect)) && (!f.gc || picks(f.gc).includes(gcOf(r))) && matches(r, q));
  const setOne = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const filter = { value: f, options: filterOptions, separator: SEP, onChange: setOne };
  const active = [f.gc, f.architect, f.scope, f.stage].filter(Boolean).length;
  const rows = filtered.filter((r) => !r.is_archived);
  const archived = filtered.filter((r) => r.is_archived);
  const table = (rs) => <SpecTrackerTable rows={rs} extraColumns={extraColumns} onPatch={onPatch || bod.patch} onNote={bod.addNote} onContact={bod.saveContact} onGcContacts={bod.saveGcContacts} onEmail={bod.setEmailRow} onGcAdded={bod.load} filter={filter} />;
  if (toolbar) {
    // Sales › Basis of Design: stat band → view pills toolbar → grid card (UI_PATTERNS.md "view pills").
    const shown = view === "archived" ? archived : rows;
    const pill = (key, label, multi = true) => ({ key: `bod-${key}`, label, multi, separator: SEP, options: filterOptions[key],
                                                 value: f[key], onChange: (v) => setOne(key, v) });
    return (
      <div className="flex flex-col gap-3">
        {stats && <BodStats rows={bod.rows} />}
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="bod-toolbar">
          <SearchBar value={search} onChange={setSearch} placeholder="Search..." testId="bod-search" />
          <Segmented options={[{ key: "active", label: "Active", testId: "bod-view-active" },
                               { key: "archived", label: "Archived", count: archived.length || undefined, testId: "bod-view-archived" }]}
                     value={view} onChange={setView}>
            <span className="mx-0.5 h-4 w-px bg-slate-300" />
            <Pill p={pill("stage", "Stage", false)} />
            <Pill p={pill("architect", "Architect")} />
            <Pill p={pill("gc", "GC")} />
          </Segmented>
          {(active > 0 || q) && (
            <button type="button" onClick={() => { setF(EMPTY); setSearch(""); }} data-testid="bod-filters-clear"
                    className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-red-600"><X className="h-3 w-3" /> Clear</button>)}
          <span className="ml-auto text-xs font-medium text-slate-400" data-testid="bod-count">{shown.length} row{shown.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-card">
          {shown.length === 0
            ? <div className="p-4 text-sm text-muted-foreground" data-testid="bod-empty">{bod.rows.length ? (view === "archived" ? "Nothing archived matches." : "No open rows match.") : emptyText}</div>
            : table(shown)}
        </div>
      </div>
    );
  }
  return (
    <>
      {rows.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground" data-testid="bod-empty">{bod.rows.length ? "No open rows match." : emptyText}</div>
      ) : table(rows)}
      {archived.length > 0 && (
        <div className="border-t border-border" data-testid="bod-archive">
          <button type="button" onClick={() => setArchOpen(!archOpen)} data-testid="bod-archive-toggle"
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
            {archOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Archive className="h-3.5 w-3.5" /> Archive <span className="font-normal" data-testid="bod-archive-count">· {archived.length}</span>
          </button>
          {archOpen && table(archived)}
        </div>)}
    </>
  );
}
